/**
 * What the customer told us about their pets.
 *
 * More than one answer is possible — a household with a dog and a cat had to
 * pick one of them before this, and the cleaner found out about the other on
 * arrival. The store keeps the shape it always had, `{ type, text }`, with
 * `type` holding the choices joined by ", ": an older saved booking with a
 * single "Dog" still reads back as exactly one choice, and every consumer that
 * treats `pets` as a sentence (the API column, the dashboard's creatable
 * select) keeps working without being taught about arrays.
 */

/** The choice that means "none", which cannot be held with any other. */
export const NO_PETS = "No Pets";

/** The choice that opens the free-text field. */
export const OTHER_PETS = "Others";

const SEPARATOR = ", ";

/** The stored string as a list of choices. */
export function petsList(type: string): string[] {
  return type
    .split(",")
    .map((pet) => pet.trim())
    .filter(Boolean);
}

/** A list of choices as the store holds it. */
export function petsValue(pets: string[]): string {
  return pets.join(SEPARATOR);
}

/**
 * The selection after clicking `pet`, given what was already chosen.
 *
 * "No Pets" is exclusive in both directions: choosing it drops the animals, and
 * choosing an animal drops it. Unchecking the last choice leaves nothing
 * selected rather than silently reinstating "No Pets" — "I have not answered"
 * and "I have no pets" are different things, and the step asks for an answer
 * before it lets the customer on.
 */
export function togglePet(chosen: string[], pet: string): string[] {
  if (chosen.includes(pet)) return chosen.filter((p) => p !== pet);
  if (pet === NO_PETS) return [NO_PETS];
  return [...chosen.filter((p) => p !== NO_PETS), pet];
}

/**
 * The one line the cleaner reads, from the choices and the free text.
 *
 * "Others" is replaced by what was typed, rather than appended to it: the point
 * of the text box is to say which other animal, so "Dog, Parrot" is the answer
 * and "Dog, Others, Parrot" is the same answer with a filing category left in.
 * With nothing typed the word stands, so the cleaner still learns there is an
 * animal we have no name for.
 */
export function petsLabel(pets: { type: string; text: string }): string {
  const text = (pets.text ?? "").trim();
  return petsValue(
    petsList(pets.type ?? "").map((pet) =>
      pet === OTHER_PETS && text ? text : pet
    )
  );
}
